/**
 * Utility function για τη μορφοποίηση ονόματος υπόθεσης
 * 
 * Κανόνες ονοματοδοσίας:
 * - Αστική/Διοικητική: [Περιγραφή υπόθεσης] / Αντίδικος: [Στοιχεία αντιδίκου] / ΓΑΚ: [ΓΑΚ]
 * - Ποινική: [Δικαστήριο] / [Ημερομηνία δικάσιμου]
 */

// Case description labels mapping
const CASE_DESCRIPTION_LABELS = {
  'debt': 'Αγωγή χρέους',
  'property': 'Εμπράγματη αγωγή',
  'fraud': 'Αγωγή καταδολίευσης',
  'inheritance': 'Κληρονομική διαφορά',
  'divorce': 'Διαζύγιο',
  'alimony': 'Διατροφή',
  'custody': 'Επιμέλεια τέκνων',
  'lease': 'Μισθωτική διαφορά',
  'labor': 'Εργατική διαφορά',
  'tort': 'Αδικοπραξία',
  'contract': 'Συμβατική διαφορά',
  'unjust_enrichment': 'Αδικαιολόγητος πλουτισμός',
  'compensation': 'Αποζημίωση',
  'ownership': 'Αναγνώριση κυριότητας',
  'possession': 'Προστασία νομής',
  'easement': 'Δουλεία',
  'condominium': 'Διαφορά οροφοκτησίας',
  'insurance': 'Ασφαλιστική διαφορά',
  'medical': 'Ιατρική αμέλεια',
  'traffic': 'Τροχαίο ατύχημα',
  'other': 'Άλλο'
};

// Mapping δικαστηρίων για ποινικές υποθέσεις
const CRIMINAL_COURT_NAMES = {
  'single_criminal': 'Μονομελές Πλημμελειοδικείο',
  'three_criminal': 'Τριμελές Πλημμελειοδικείο',
  'single_criminal_appeal': 'Μονομελές Εφετείο Κακουργημάτων',
  'triple_criminal_appeal': 'Τριμελές Εφετείο Κακουργημάτων',
  'five_criminal_appeal': 'Πενταμελές Εφετείο',
  'mixed_court': 'Μικτό Ορκωτό Δικαστήριο',
  'mixed_jury': 'Μικτό Ορκωτό',
  'appeal_criminal': 'Εφετείο Κακουργημάτων',
  'supreme_criminal': 'Άρειος Πάγος (Ποινικό)',
};

// Mapping τύπων δικαστηρίων για αστικές υποθέσεις
const CIVIL_COURT_NAMES = {
  'single_first': 'Μονομελές Πρωτοδικείο',
  'multi_first': 'Πολυμελές Πρωτοδικείο',
  'single_appeal': 'Μονομελές Εφετείο',
  'triple_appeal': 'Τριμελές Εφετείο',
  'supreme': 'Άρειος Πάγος',
  'peace_court': 'Ειρηνοδικείο'
};

// Mapping τύπων διοικητικών δικαστηρίων
const ADMIN_COURT_NAMES = {
  'single_admin_first': 'Μονομελές Διοικητικό Πρωτοδικείο',
  'triple_admin_first': 'Τριμελές Διοικητικό Πρωτοδικείο',
  'admin_appeal': 'Διοικητικό Εφετείο',
  'council_of_state': 'Συμβούλιο της Επικρατείας'
};

/**
 * Μορφοποιεί την ημερομηνία σε ελληνική μορφή (ΗΗ/ΜΜ/ΕΕΕΕ)
 */
const formatDateGreek = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('el-GR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

/**
 * Κύρια function μορφοποίησης ονόματος υπόθεσης
 * 
 * @param {Object} caseObj - Το αντικείμενο της υπόθεσης
 * @param {Array} entries - (Προαιρετικό) Λίστα καταχωρήσεων για να βρούμε hearing_date
 * @returns {string} - Το μορφοποιημένο όνομα της υπόθεσης
 */
export const formatCaseName = (caseObj, entries = []) => {
  if (!caseObj) return 'Χωρίς περιγραφή';

  const procedureType = caseObj.procedure_type;

  // ΠΟΙΝΙΚΗ ΥΠΟΘΕΣΗ: [Δικαστήριο] / [Ημερομηνία δικασίμου]
  if (procedureType === 'criminal') {
    const parts = [];
    
    // Δικαστήριο
    const courtName = CRIMINAL_COURT_NAMES[caseObj.court_type];
    if (courtName) {
      parts.push(courtName);
      if (caseObj.court_location) {
        parts[parts.length - 1] += ` ${caseObj.court_location}`;
      }
    }
    
    // Ημερομηνία δικασίμου - πρώτα από το case, αλλιώς από τα entries
    let hearingDate = caseObj.hearing_date;
    
    // Αν δεν υπάρχει στο case, ψάχνουμε στα entries για ΔΙΚΑΣΙΜΟΣ entry
    if (!hearingDate && entries && entries.length > 0) {
      const hearingEntry = entries.find(e => 
        e.case_id === caseObj.id && 
        (e.title === 'ΔΙΚΑΣΙΜΟΣ' || e.title === 'Δικάσιμος')
      );
      if (hearingEntry) {
        hearingDate = hearingEntry.date;
      }
    }
    
    if (hearingDate) {
      parts.push(formatDateGreek(hearingDate));
    }
    
    // Αν δεν έχουμε τίποτα, επιστρέφουμε fallback
    if (parts.length === 0) {
      return caseObj.description || 'Ποινική υπόθεση';
    }
    
    return parts.join(' | ');
  }

  // ΑΣΤΙΚΗ / ΔΙΟΙΚΗΤΙΚΗ ΥΠΟΘΕΣΗ: [Περιγραφή υπόθεσης] | Αντίδικος: [Στοιχεία αντιδίκου] | ΓΑΚ: [ΓΑΚ]
  const parts = [];
  
  // Περιγραφή υπόθεσης (case_description_type) - από τα entries
  let caseDescLabel = null;
  if (entries && entries.length > 0) {
    const caseEntry = entries.find(e => e.case_id === caseObj.id && e.case_description_type);
    if (caseEntry) {
      if (caseEntry.case_description_type === 'other' && caseEntry.case_description_custom) {
        caseDescLabel = caseEntry.case_description_custom;
      } else {
        caseDescLabel = CASE_DESCRIPTION_LABELS[caseEntry.case_description_type] || null;
      }
    }
  }
  
  // Fallback to caseObj.description if no case_description_type found
  if (caseDescLabel) {
    parts.push(caseDescLabel);
  } else if (caseObj.description) {
    parts.push(caseObj.description);
  }
  
  // Στοιχεία αντιδίκου - πρώτα από το case, αλλιώς από τα entries
  let opponentDetails = caseObj.opponent_details;
  if (!opponentDetails && entries && entries.length > 0) {
    const entryWithOpponent = entries.find(e => e.case_id === caseObj.id && e.opponent_details);
    if (entryWithOpponent) {
      opponentDetails = entryWithOpponent.opponent_details;
    }
  }
  if (opponentDetails) {
    parts.push(`Αντίδικος: ${opponentDetails}`);
  }
  
  // ΓΑΚ (Γενικός αριθμός κατάθεσης)
  if (caseObj.gak) {
    parts.push(`ΓΑΚ: ${caseObj.gak}`);
  }
  
  // Αν δεν έχουμε τίποτα, επιστρέφουμε fallback
  if (parts.length === 0) {
    return 'Χωρίς περιγραφή';
  }
  
  return parts.join(' | ');
};

/**
 * Σύντομη έκδοση του ονόματος υπόθεσης (για dropdowns)
 * Περιορίζει το μήκος σε maxLength χαρακτήρες
 * 
 * @param {Object} caseObj - Το αντικείμενο της υπόθεσης
 * @param {Array} entries - (Προαιρετικό) Λίστα καταχωρήσεων
 * @param {number} maxLength - Μέγιστο μήκος χαρακτήρων (default: 50)
 * @returns {string} - Το μορφοποιημένο σύντομο όνομα
 */
export const formatCaseNameShort = (caseObj, entries = [], maxLength = 50) => {
  const fullName = formatCaseName(caseObj, entries);
  
  if (fullName.length <= maxLength) {
    return fullName;
  }
  
  return fullName.substring(0, maxLength - 3) + '...';
};

export default formatCaseName;
